import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useEscapeToClose } from "../../../hooks/useEscapeToClose";
import styles from "./ConfirmDialog.module.css";

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  variant = "destructive",
  onConfirm,
  onCancel,
  portalContainer = null,
}) {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  useEscapeToClose({ enabled: open, onClose: onCancel });

  if (!open) return null;

  const dialogNode = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className={styles.dialog}
      >
        <span className={styles.handle} aria-hidden="true" />

        <h3 id="confirm-dialog-title" className={styles.title}>
          {title}
        </h3>

        <p id="confirm-dialog-message" className={styles.message}>
          {message}
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={[
              styles.confirmButton,
              variant === "neutral" && styles.confirmButtonNeutral,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>

          <button
            ref={cancelRef}
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return dialogNode;
  }

  // Dans un <dialog> modal natif (couche supérieure du navigateur), la
  // confirmation doit être rendue à l'intérieur, sinon elle reste derrière.
  return createPortal(dialogNode, portalContainer || document.body);
}
